/**
 * Cycle Participants Store
 *
 * Manages participants in DEGAP cycles via the normalized `cycle_participants` table.
 * This replaces the JSONB-based participant storage in degap_cycles.outputs.engage.participants.
 *
 * Per the Authoritative Cycles implementation plan:
 * - Participants are stored in a normalized table for proper querying
 * - ENGAGE gates predicate on active participants with decision rights
 * - All participant changes emit governance events for audit trail
 *
 * Note: cycle_participants.role is a display label for the participant roster.
 * It no longer gates UI surface routing — all access decisions run through
 * the primary RBAC system (role_assignments / permissionResolver).
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, generateUUID } from '../../lib/supabaseSync';

// ============================================================================
// Types
// ============================================================================

export type ParticipantRole = 'facilitator' | 'steward' | 'contributor' | 'observer' | 'criteria_authority';
/** Stage-based roles (S1–S6): who does what. A participant can have multiple. Legacy role is unchanged. */
export type StageRole =
  | 'stakeholder'       // Notify to submit items (intake)
  | 'item_pool_selector'      // S2: who selects/locks the pool
  | 'criteria_selector'       // S3: who selects criteria
  | 'criteria_value_selector' // S4: who defines value scales
  | 'value_provider'          // S5: who provides values (can be refined per-attribute)
  | 'session_runner';         // S6: who runs prioritization sessions
export type ParticipantScope = 'executive' | 'middle' | 'bottom';
export type ParticipantStatus = 'invited' | 'active' | 'declined' | 'removed';

export interface CycleParticipant {
  id: string;
  cycleId: string;
  userId: string;
  role: ParticipantRole;
  scope?: ParticipantScope;
  /** Stage-based roles: stakeholder, item_pool_selector, criteria_selector, criteria_value_selector, value_provider, session_runner */
  stageRoles: StageRole[];
  decisionRights: string[]; // e.g., ['criteria_selection', 'pool_approval', 'cut_line']
  status: ParticipantStatus;
  addedBy: string;
  addedAt: Date;
  acceptedAt?: Date;
  removedAt?: Date;
  removedBy?: string;
  removalReason?: string;
  workspaceId?: string;
}

interface CycleParticipantsState {
  participants: CycleParticipant[];
  isLoading: boolean;
  error: string | null;

  // CRUD operations
  fetchParticipantsForCycle: (cycleId: string) => Promise<void>;
  addParticipant: (input: {
    cycleId: string;
    userId: string;
    role: ParticipantRole;
    scope?: ParticipantScope;
    decisionRights?: string[];
    addedBy: string;
    workspaceId?: string;
  }) => Promise<CycleParticipant | null>;
  updateParticipant: (id: string, updates: Partial<Pick<CycleParticipant, 'role' | 'scope' | 'stageRoles' | 'decisionRights' | 'status'>>) => Promise<boolean>;
  activateParticipant: (id: string) => Promise<boolean>;
  removeParticipant: (id: string, removedBy: string, reason?: string) => Promise<boolean>;

  // Selectors
  getParticipantsForCycle: (cycleId: string) => CycleParticipant[];
  getActiveParticipantsForCycle: (cycleId: string) => CycleParticipant[];
  getActiveParticipantsWithRights: (cycleId: string) => CycleParticipant[];
  getParticipantById: (id: string) => CycleParticipant | undefined;
}

// ============================================================================
// Database Helpers
// ============================================================================

const STAGE_ROLES: StageRole[] = ['stakeholder', 'item_pool_selector', 'criteria_selector', 'criteria_value_selector', 'value_provider', 'session_runner'];

function dbToParticipant(row: Record<string, unknown>): CycleParticipant {
  const rawStageRoles = (row.stage_roles as string[] | undefined) || [];
  const stageRoles = rawStageRoles.filter((r): r is StageRole => STAGE_ROLES.includes(r as StageRole));
  return {
    id: row.id as string,
    cycleId: row.cycle_id as string,
    userId: row.user_id as string,
    role: row.role as ParticipantRole,
    scope: row.scope as ParticipantScope | undefined,
    stageRoles,
    decisionRights: (row.decision_rights as string[]) || [],
    status: row.status as ParticipantStatus,
    addedBy: row.added_by as string,
    addedAt: row.added_at != null && String(row.added_at).length > 0
      ? new Date(row.added_at as string)
      : new Date(0),
    acceptedAt: row.accepted_at ? new Date(row.accepted_at as string) : undefined,
    removedAt: row.removed_at ? new Date(row.removed_at as string) : undefined,
    removedBy: row.removed_by as string | undefined,
    removalReason: row.removal_reason as string | undefined,
    workspaceId: row.workspace_id as string | undefined,
  };
}

function participantToDb(participant: Partial<CycleParticipant>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  
  if (participant.id !== undefined) result.id = participant.id;
  if (participant.cycleId !== undefined) result.cycle_id = participant.cycleId;
  if (participant.userId !== undefined) result.user_id = participant.userId;
  if (participant.role !== undefined) result.role = participant.role;
  if (participant.scope !== undefined) result.scope = participant.scope;
  if (participant.stageRoles !== undefined) result.stage_roles = participant.stageRoles;
  if (participant.decisionRights !== undefined) result.decision_rights = participant.decisionRights;
  if (participant.status !== undefined) result.status = participant.status;
  if (participant.addedBy !== undefined) result.added_by = participant.addedBy;
  if (participant.addedAt !== undefined) result.added_at = participant.addedAt.toISOString();
  if (participant.acceptedAt !== undefined) result.accepted_at = participant.acceptedAt.toISOString();
  if (participant.removedAt !== undefined) result.removed_at = participant.removedAt.toISOString();
  if (participant.removedBy !== undefined) result.removed_by = participant.removedBy;
  if (participant.removalReason !== undefined) result.removal_reason = participant.removalReason;
  if (participant.workspaceId !== undefined) result.workspace_id = participant.workspaceId;
  
  return result;
}

// ============================================================================
// Governance Event Helper
// IMPORTANT: Best-effort, non-blocking. Must NEVER prevent participant operations.
// ============================================================================

async function recordParticipantEvent(
  eventType: 'participant_added' | 'participant_activated' | 'participant_removed' | 'participant_updated',
  cycleId: string,
  actorId: string,
  details?: Record<string, unknown>
): Promise<void> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    console.warn('[CycleParticipantsStore] Governance event skipped: Supabase unavailable');
    return;
  }

  try {
    const { error } = await supabase.from('governance_events').insert({
      id: generateUUID(),
      cycle_id: cycleId,
      event_type: eventType,
      actor_id: actorId,
      occurred_at: new Date().toISOString(),
      note: details ? JSON.stringify(details) : null,
    });
    
    if (error) {
      // Log but DO NOT throw - this is best-effort
      console.warn(`[CycleParticipantsStore] Governance event failed (non-blocking): ${error.message}`);
    }
  } catch (err) {
    // Catch ALL errors - governance must NEVER block participant operations
    console.warn('[CycleParticipantsStore] Governance event exception (non-blocking):', err);
  }
}

// ============================================================================
// Store
// ============================================================================

export const useCycleParticipantsStore = create<CycleParticipantsState>()((set, get) => ({
  participants: [],
  isLoading: false,
  error: null,

  fetchParticipantsForCycle: async (cycleId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      console.warn('[CycleParticipantsStore] Supabase not available');
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase
        .from('cycle_participants')
        .select('*')
        .eq('cycle_id', cycleId)
        .order('added_at', { ascending: true });

      if (error) {
        console.error('[CycleParticipantsStore] Fetch error:', error);
        set({ isLoading: false, error: error.message });
        return;
      }

      const fetched = (data || []).map(dbToParticipant);
      
      // Merge with existing (replace those for this cycle)
      set(state => {
        const other = state.participants.filter(p => p.cycleId !== cycleId);
        return {
          participants: [...other, ...fetched],
          isLoading: false,
          error: null,
        };
      });

      console.log(`[CycleParticipantsStore] Fetched ${fetched.length} participants for cycle ${cycleId}`);
    } catch (err) {
      console.error('[CycleParticipantsStore] Fetch exception:', err);
      set({ isLoading: false, error: String(err) });
    }
  },

  addParticipant: async (input) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      console.warn('[CycleParticipantsStore] Supabase not available');
      return null;
    }

    try {
      const now = new Date().toISOString();
      const id = generateUUID();
      const { error } = await supabase.from('cycle_participants').upsert(
        {
          id,
          cycle_id: input.cycleId,
          user_id: input.userId,
          role: input.role ?? 'contributor',
          scope: input.scope ?? null,
          decision_rights: input.decisionRights ?? [],
          status: 'active',
          added_by: input.addedBy,
          added_at: now,
          accepted_at: now,
          workspace_id: input.workspaceId ?? null,
        },
        { onConflict: 'cycle_id,user_id', ignoreDuplicates: false },
      );

      if (error) {
        console.error('[CycleParticipantsStore] addParticipant error:', error);
        set({ error: error.message });
        return null;
      }

      await get().fetchParticipantsForCycle(input.cycleId);

      const participant = get().participants.find(
        (p) => p.userId === input.userId && p.cycleId === input.cycleId,
      );

      await recordParticipantEvent('participant_added', input.cycleId, input.addedBy, {
        userId: input.userId,
        role: input.role,
        scope: input.scope,
      });

      console.log('[CycleParticipantsStore] Added participant:', participant?.id);
      return participant ?? null;
    } catch (err) {
      console.error('[CycleParticipantsStore] addParticipant exception:', err);
      set({ error: String(err) });
      return null;
    }
  },

  updateParticipant: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const participant = get().getParticipantById(id);
    if (!participant) return false;

    try {
      const { error } = await supabase
        .from('cycle_participants')
        .update(participantToDb(updates))
        .eq('id', id);

      if (error) {
        console.error('[CycleParticipantsStore] Update error:', error);
        return false;
      }

      set((state) => ({
        participants: state.participants.map((p) =>
          p.id === id ? { ...p, ...updates } : p,
        ),
      }));

      console.log('[CycleParticipantsStore] Updated participant:', id);
      return true;
    } catch (err) {
      console.error('[CycleParticipantsStore] Update exception:', err);
      return false;
    }
  },

  activateParticipant: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const participant = get().getParticipantById(id);
    if (!participant) return false;

    const now = new Date();

    try {
      const { error } = await supabase
        .from('cycle_participants')
        .update({ status: 'active', accepted_at: now.toISOString() })
        .eq('id', id);

      if (error) {
        console.error('[CycleParticipantsStore] Activate error:', error);
        return false;
      }

      set(state => ({
        participants: state.participants.map(p =>
          p.id === id ? { ...p, status: 'active' as ParticipantStatus, acceptedAt: now } : p
        ),
      }));

      await recordParticipantEvent('participant_activated', participant.cycleId, participant.userId);
      console.log('[CycleParticipantsStore] Activated participant:', id);
      return true;
    } catch (err) {
      console.error('[CycleParticipantsStore] Activate exception:', err);
      return false;
    }
  },

  removeParticipant: async (id, removedBy, reason) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const participant = get().getParticipantById(id);
    if (!participant) return false;

    const now = new Date();

    try {
      const { error } = await supabase
        .from('cycle_participants')
        .update({
          status: 'removed',
          removed_at: now.toISOString(),
          removed_by: removedBy,
          removal_reason: reason,
        })
        .eq('id', id);

      if (error) {
        console.error('[CycleParticipantsStore] Remove error:', error);
        return false;
      }

      set(state => ({
        participants: state.participants.map(p =>
          p.id === id
            ? { ...p, status: 'removed' as ParticipantStatus, removedAt: now, removedBy, removalReason: reason }
            : p
        ),
      }));

      await recordParticipantEvent('participant_removed', participant.cycleId, removedBy, {
        userId: participant.userId,
        reason,
      });

      console.log('[CycleParticipantsStore] Removed participant:', id);
      return true;
    } catch (err) {
      console.error('[CycleParticipantsStore] Remove exception:', err);
      return false;
    }
  },

  // Selectors
  getParticipantsForCycle: (cycleId) => {
    return get().participants.filter(p => p.cycleId === cycleId);
  },

  getActiveParticipantsForCycle: (cycleId) => {
    return get().participants.filter(p => p.cycleId === cycleId && p.status === 'active');
  },

  getActiveParticipantsWithRights: (cycleId) => {
    return get().participants.filter(
      p => p.cycleId === cycleId && p.status === 'active' && p.decisionRights.length > 0
    );
  },

  getParticipantById: (id) => {
    return get().participants.find(p => p.id === id);
  },
}));
